import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, ShieldCheck } from "lucide-react";
import { Button } from "./Button";

export function FishingPreferences() {
  const queryClient = useQueryClient();
  const preferencesQuery = useQuery({
    queryKey: ["catchcompass_preferences"],
    queryFn: () => fetch("/app-api/preferences").then((r) => {
      if (!r.ok) throw new Error("Could not load fishing defaults");
      return r.json();
    }),
  });
  const [privacy, setPrivacy] = useState("Contribute anonymously to CarpIntel statistics");
  const [sharePeg, setSharePeg] = useState(false);
  const [duration, setDuration] = useState("24");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const data = preferencesQuery.data;
    if (!data) return;
    setPrivacy(data.default_privacy_setting || "Contribute anonymously to CarpIntel statistics");
    setSharePeg(data.default_share_peg_anonymously === true);
    const stored = String(data.default_session_duration_hours ?? 24);
    setDuration(["6", "12", "24", "48"].includes(stored) ? stored : "24");
  }, [preferencesQuery.data]);

  async function save() {
    setSaving(true); setMessage("");
    try {
      const response = await fetch("/app-api/preferences", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          default_privacy_setting: privacy,
          default_share_peg_anonymously: sharePeg,
          default_session_duration_hours: Number(duration || 24),
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not save defaults");
      await queryClient.invalidateQueries({ queryKey: ["catchcompass_preferences"] });
      setMessage("Fishing defaults saved.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not save defaults"); }
    finally { setSaving(false); }
  }

  async function sendTest() {
    setTesting(true); setMessage("");
    try {
      const response = await fetch("/app-api/push/test", { method: "POST" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not send test notification");
      setMessage("Test notification sent. If iOS notifications are allowed, it should arrive within a few seconds.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not send test notification"); }
    finally { setTesting(false); }
  }

  return (
    <section className="space-y-5 rounded-xl border border-border bg-raised p-5">
      <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 shrink-0 text-accent" /><div><h2 className="font-semibold text-primary">Fishing defaults</h2><p className="mt-1 text-sm leading-6 text-secondary">Set these once. New sessions inherit them so CatchCompass does not keep asking the same questions.</p></div></div>
      <label className="block text-sm font-medium text-primary">Default privacy<select value={privacy} onChange={(e) => setPrivacy(e.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="Private">Private</option><option value="Contribute anonymously to CarpIntel statistics">Contribute anonymously</option><option value="Shared">Shared</option></select></label>
      <label className="flex items-center gap-2 text-sm text-secondary"><input type="checkbox" checked={sharePeg} onChange={(e) => setSharePeg(e.target.checked)} /> Allow peg name to contribute anonymously by default</label>
      <label className="block text-sm font-medium text-primary">Usual session length<select value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-primary"><option value="6">6 hours</option><option value="12">12 hours</option><option value="24">24 hours</option><option value="48">48 hours</option></select></label>
      <p className="text-xs text-secondary">You can still choose Custom when starting an individual session.</p>
      <Button type="button" onClick={save} isLoading={saving}>Save fishing defaults</Button>
      <div className="border-t border-border pt-4"><div className="flex items-start gap-3"><BellRing className="mt-0.5 size-5 shrink-0 text-accent" /><div><p className="font-semibold text-primary">Test session alerts</p><p className="mt-1 text-xs leading-5 text-secondary">Enable Session alerts above first, then use this to prove the complete push-notification chain immediately.</p></div></div><Button type="button" variant="secondary" className="mt-3" onClick={sendTest} isLoading={testing}>Send test notification</Button></div>
      {message ? <p className="rounded-lg border border-border bg-background p-3 text-sm text-secondary" role="status">{message}</p> : null}
    </section>
  );
}
